// WOS-BUNDLE: the top of Work's sidebar — FAVORITES (the person's stars on the server, in their
// order: spaces, boards, docs, tasks and channels) and RECENT (the last ten things they opened,
// collapsible). A favorite row shows on hover a grip (drag it before another row), Move up and
// Move down icons (wp/ACTIONS), a "…" menu
// (Move up, Move down, Remove from favorites) and an unstar ×; Alt+Shift+↑/↓ on a focused row
// moves it too (BOARD-ORDER's keys). Every move and unstar shows at once; a refusal undoes it
// with "Couldn't update favorites" (favorites.ts).
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  NavList,
  NavRow,
  SidebarCaption,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckSquareIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisIcon,
  FileTextIcon,
  GripVerticalIcon,
  HashIcon,
  type LucideIcon,
  SquareKanbanIcon,
  StarOffIcon,
  XIcon,
} from "lucide-react";
import { type ReactElement, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../../features/company";
import { type FavoriteView, reorderFavorites, useFavoritesOf } from "../../features/work/favorites";
import { type RecentView, recentsQuery } from "../../features/work/recents";
import { SPACE_ICON } from "../../features/work/spaces-tree";
import { keepFocus } from "../../focus/focus";

type Kind = FavoriteView["kind"];
const KIND_ICON: Record<Kind, LucideIcon> = {
  space: SPACE_ICON.team,
  board: SquareKanbanIcon,
  page: FileTextIcon,
  task: CheckSquareIcon,
  channel: HashIcon,
};

/** Where a starred or recent thing opens. */
function linkFor(slug: string, kind: Kind, id: string, boardId: string | null): ReactElement {
  const common = { search: keepFocus, activeOptions: { includeSearch: false } } as const;
  switch (kind) {
    case "space":
      return <Link to="/c/$slug/work/s/$spaceId" params={{ slug, spaceId: id }} {...common} />;
    case "board":
      return <Link to="/c/$slug/work/b/$boardId" params={{ slug, boardId: id }} {...common} />;
    case "page":
      return <Link to="/c/$slug/work/p/$pageId" params={{ slug, pageId: id }} {...common} />;
    case "channel":
      return <Link to="/c/$slug/chat/$channelId" params={{ slug, channelId: id }} {...common} />;
    case "task":
      return boardId ? (
        <Link
          to="/c/$slug/work/b/$boardId"
          params={{ slug, boardId }}
          search={{ task: id } as never}
          activeOptions={{ includeSearch: true }}
        />
      ) : (
        <Link to="/c/$slug/work" params={{ slug }} search={{ task: id } as never} />
      );
  }
}

const HOVER =
  "opacity-0 group-hover/nav-row:opacity-100 group-focus-within/nav-row:opacity-100 pointer-coarse:opacity-100";

/** SIDEBAR-TOGGLE-FIXED: the favorites on the collapsed strip, one icon each (name on hover). */
export function WorkFavoritesStrip({ api, company }: { api: Api; company: CompanyAccess }) {
  const favorites = useFavoritesOf(api, company.id);
  const rows = favorites.views;
  if (!favorites.server || !rows.length) return null;
  return (
    <>
      <SidebarCaption label="Favorites" />
      <NavList aria-label="Favorites">
        {rows.map((f) => (
          <NavRow
            key={`${f.kind}:${f.id}`}
            icon={KIND_ICON[f.kind]}
            label={f.kind === "channel" ? `#${f.title}` : f.title}
            render={linkFor(company.slug, f.kind, f.id, f.board_id)}
          />
        ))}
      </NavList>
    </>
  );
}

export function WorkFavorites({ api, company }: { api: Api; company: CompanyAccess }) {
  const favorites = useFavoritesOf(api, company.id);
  const rows = favorites.views;
  const [dragging, setDragging] = useState<number | null>(null);
  const [over, setOver] = useState<number | null>(null);
  if (!favorites.server || !rows.length) return null;
  const move = (from: number, to: number) => {
    if (to < 0 || to >= rows.length || from === to) return;
    favorites.reorder(reorderFavorites(rows, from, to));
  };
  return (
    <>
      <SidebarCaption label="Favorites" />
      <NavList aria-label="Favorites" data-slot="sidebar-favorites">
        {rows.map((f, i) => (
          <NavRow
            key={`${f.kind}:${f.id}`}
            icon={KIND_ICON[f.kind]}
            label={f.kind === "channel" ? `#${f.title}` : f.title}
            data-favorite={`${f.kind}:${f.id}`}
            data-drop-target={over === i && dragging !== null && dragging !== i ? "" : undefined}
            className="data-drop-target:shadow-[inset_0_2px_0_var(--primary)]"
            draggable
            onDragStart={(e) => {
              setDragging(i);
              e.dataTransfer.effectAllowed = "move";
              e.dataTransfer.setData("text/plain", `${f.kind}:${f.id}`);
            }}
            onDragOver={(e) => {
              if (dragging === null) return;
              e.preventDefault();
              setOver(i);
            }}
            onDragLeave={() => setOver((o) => (o === i ? null : o))}
            onDrop={(e) => {
              e.preventDefault();
              if (dragging !== null) move(dragging, i);
              setDragging(null);
              setOver(null);
            }}
            onDragEnd={() => {
              setDragging(null);
              setOver(null);
            }}
            onKeyDown={(e) => {
              if (!e.altKey || !e.shiftKey) return;
              if (e.key === "ArrowUp") {
                e.preventDefault();
                move(i, i - 1);
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                move(i, i + 1);
              }
            }}
            aria-keyshortcuts="Alt+Shift+ArrowUp Alt+Shift+ArrowDown"
            lead={
              <GripVerticalIcon
                aria-hidden="true"
                data-slot="favorite-grip"
                className={`absolute left-0 size-3.5 cursor-grab text-faint ${HOVER}`}
              />
            }
            trailing={
              // React events from the menu's portal bubble through here: they must not reach the
              // row's link (a "Move up" click would open the favorite).
              // biome-ignore lint/a11y/noStaticElementInteractions: a bubbling guard, not a control.
              // biome-ignore lint/a11y/useKeyWithClickEvents: the controls inside take the keys.
              <span
                className="relative z-10 flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                {/* wp/ACTIONS: Move up / down as visible hover icons (the menu keeps them too). */}
                {rows.length > 1 ? (
                  <>
                    <IconButton
                      label={`Move ${f.title} up`}
                      tip="Move up"
                      icon={ArrowUpIcon}
                      disabled={i === 0}
                      onClick={() => move(i, i - 1)}
                    />
                    <IconButton
                      label={`Move ${f.title} down`}
                      tip="Move down"
                      icon={ArrowDownIcon}
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, i + 1)}
                    />
                  </>
                ) : null}
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Actions for favorite ${f.title}`}
                        className={HOVER}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                        }}
                      />
                    }
                  >
                    <EllipsisIcon aria-hidden="true" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start">
                    <DropdownMenuItem disabled={i === 0} onClick={() => move(i, i - 1)}>
                      <ArrowUpIcon aria-hidden="true" />
                      Move up
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={i === rows.length - 1}
                      onClick={() => move(i, i + 1)}
                    >
                      <ArrowDownIcon aria-hidden="true" />
                      Move down
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => favorites.toggle(f, f.title)}>
                      <StarOffIcon aria-hidden="true" />
                      Remove from favorites
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon-xs"
                        aria-label={`Remove ${f.title} from favorites`}
                        className={HOVER}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          favorites.toggle(f, f.title);
                        }}
                      />
                    }
                  >
                    <XIcon aria-hidden="true" />
                  </TooltipTrigger>
                  <TooltipContent>Remove from favorites</TooltipContent>
                </Tooltip>
              </span>
            }
            render={linkFor(company.slug, f.kind, f.id, f.board_id)}
          />
        ))}
      </NavList>
    </>
  );
}

/** A favorite row's hover icon (a 24 px ghost button with its tooltip). */
function IconButton({
  label,
  tip,
  icon: Icon,
  disabled,
  onClick,
}: {
  label: string;
  tip: string;
  icon: LucideIcon;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={label}
            disabled={disabled}
            className={HOVER}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClick();
            }}
          />
        }
      >
        <Icon aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>{tip}</TooltipContent>
    </Tooltip>
  );
}

const RECENT_OPEN = "companyos.work.recent-open";

export function WorkRecents({ api, company }: { api: Api; company: CompanyAccess }) {
  const recents = useQuery(recentsQuery(api, company.id));
  const [open, setOpen] = useState(() => {
    try {
      return window.localStorage.getItem(RECENT_OPEN) !== "0";
    } catch {
      return true;
    }
  });
  const rows: RecentView[] = recents.data ?? [];
  if (!rows.length) return null;
  const toggle = () => {
    setOpen(!open);
    try {
      window.localStorage.setItem(RECENT_OPEN, open ? "0" : "1");
    } catch {}
  };
  return (
    <>
      <SidebarCaption
        label="Recent"
        action={
          <Button
            variant="ghost"
            size="icon-xs"
            aria-expanded={open}
            aria-label={open ? "Collapse Recent" : "Expand Recent"}
            onClick={toggle}
          >
            {open ? (
              <ChevronDownIcon aria-hidden="true" />
            ) : (
              <ChevronRightIcon aria-hidden="true" />
            )}
          </Button>
        }
      />
      {open ? (
        <NavList aria-label="Recent" data-slot="sidebar-recents">
          {rows.slice(0, 6).map((r) => (
            <NavRow
              key={`${r.kind}:${r.id}`}
              icon={KIND_ICON[r.kind]}
              label={r.title}
              title={[...r.path, r.title].join(" › ")}
              render={linkFor(company.slug, r.kind, r.id, r.board_id)}
            />
          ))}
        </NavList>
      ) : null}
    </>
  );
}
